/**
 * Contact Page
 * Premium contact page with multiple contact methods and form
 */

import { useState } from 'react';
import { Layout } from '~/components/layout/Layout';
import { motion } from 'motion/react';
import {
  IconPhone,
  IconChat,
  IconArrowRight,
  IconCheck,
  IconHeadset,
  IconClock,
} from '~/components/ui/Icons';
import { contactContent, siteConfig } from '~/lib/cms/content';

// Email icon component
function IconEmail({ size = 24, className = '' }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M22 6l-10 7L2 6" />
    </svg>
  );
}

// Location icon component
function IconLocation({ size = 24, className = '' }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

export function ContactPage() {
  const content = contactContent;
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    department: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const iconMap: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
    phone: IconPhone,
    email: IconEmail,
    chat: IconChat,
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const body = new URLSearchParams({
        'form_type': 'contact',
        'utf8': '✓',
        'contact[name]': formState.name,
        'contact[email]': formState.email,
        'contact[body]': `[${formState.department || 'General'}] ${formState.message}`,
      });

      const res = await fetch('https://tr9ath-e6.myshopify.com/contact#ContactForm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
        mode: 'no-cors',
      });

      setIsSubmitted(true);
    } catch (err) {
      console.error('Contact form error:', err);
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-surface-coal via-surface-black to-surface-graphite" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(255,116,0,0.1),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(59,130,246,0.08),transparent_50%)]" />

        {/* Grid */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />

        {/* Decorative Shapes */}
        <div className="absolute top-40 -right-20 w-80 h-80 bg-brand-orange/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />

        <div className="container-wide relative py-20 lg:py-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-orange/10 border border-brand-orange/20 rounded-full mb-6">
              <IconHeadset size={16} className="text-brand-orange" />
              <span className="text-sm font-medium text-brand-orange">We're here to help</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-accent-white mb-6">
              {content.hero.title}
            </h1>

            <p className="text-xl text-accent-silver leading-relaxed">
              {content.hero.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="container-wide py-16 -mt-6">
        <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
          {content.contactMethods.map((method, index) => {
            const Icon = iconMap[method.icon] || IconPhone;

            return (
              <motion.a
                key={method.title}
                href={method.href}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative p-8 rounded-2xl bg-surface-coal/80 backdrop-blur-xl border border-surface-slate/30 hover:border-brand-orange/40 transition-all duration-300 hover:shadow-2xl hover:shadow-brand-orange/5"
              >
                {/* Gradient Overlay */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-brand-orange/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                <div className="relative">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-orange/20 to-brand-orange/5 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Icon size={28} className="text-brand-orange" />
                  </div>

                  <h3 className="text-xl font-semibold text-accent-white mb-2">
                    {method.title}
                  </h3>

                  <p className="text-xl font-medium text-brand-orange mb-1">
                    {method.value}
                  </p>

                  <p className="text-sm text-accent-silver mb-4">
                    {method.description}
                  </p>

                  <div className="flex items-center gap-2 text-xs text-accent-silver">
                    <IconClock size={14} />
                    {method.availability}
                  </div>
                </div>

                <div className="absolute bottom-8 right-8 opacity-0 group-hover:opacity-100 transition-opacity">
                  <IconArrowRight size={20} className="text-brand-orange" />
                </div>
              </motion.a>
            );
          })}
        </div>
      </section>

      {/* Main Content Grid */}
      <section className="container-wide py-16">
        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-3"
          >
            <div className="relative p-8 md:p-10 rounded-3xl bg-surface-coal border border-surface-slate/30">
              {/* Form Glow */}
              <div className="absolute -inset-1 bg-gradient-to-r from-brand-orange/20 via-purple-500/10 to-blue-500/20 rounded-3xl blur-xl opacity-30" />

              <div className="relative">
                <h2 className="text-2xl md:text-3xl font-display font-bold text-accent-white mb-2">
                  {content.formFields.title}
                </h2>
                <p className="text-accent-silver mb-8">
                  {content.formFields.subtitle}
                </p>

                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="py-16 text-center"
                  >
                    <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                      <IconCheck size={40} className="text-emerald-400" />
                    </div>
                    <h3 className="text-2xl font-display font-bold text-accent-white mb-3">
                      Message Sent!
                    </h3>
                    <p className="text-accent-silver mb-8">
                      {content.formFields.successMessage}
                    </p>
                    <button
                      onClick={() => {
                        setIsSubmitted(false);
                        setFormState({ name: '', email: '', department: '', message: '' });
                      }}
                      className="text-brand-orange hover:text-brand-orange-light font-medium transition-colors"
                    >
                      Send another message
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-accent-platinum mb-2">
                          Your Name
                        </label>
                        <input
                          type="text"
                          required
                          value={formState.name}
                          onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                          className="w-full px-4 py-3.5 bg-surface-graphite/50 border border-surface-slate/30 rounded-xl text-accent-white placeholder:text-accent-silver/50 focus:border-brand-orange/50 focus:ring-2 focus:ring-brand-orange/20 outline-none transition-all"
                          placeholder="John Smith"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-accent-platinum mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          required
                          value={formState.email}
                          onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                          className="w-full px-4 py-3.5 bg-surface-graphite/50 border border-surface-slate/30 rounded-xl text-accent-white placeholder:text-accent-silver/50 focus:border-brand-orange/50 focus:ring-2 focus:ring-brand-orange/20 outline-none transition-all"
                          placeholder="john@company.com"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-accent-platinum mb-2">
                        Department
                      </label>
                      <select
                        value={formState.department}
                        onChange={(e) => setFormState({ ...formState, department: e.target.value })}
                        className="w-full px-4 py-3.5 bg-surface-graphite/50 border border-surface-slate/30 rounded-xl text-accent-white focus:border-brand-orange/50 focus:ring-2 focus:ring-brand-orange/20 outline-none transition-all"
                      >
                        <option value="">Select a department...</option>
                        {content.departments.map((dept) => (
                          <option key={dept.value} value={dept.value}>
                            {dept.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-accent-platinum mb-2">
                        Message
                      </label>
                      <textarea
                        required
                        rows={5}
                        value={formState.message}
                        onChange={(e) => setFormState({ ...formState, message: e.target.value })}
                        className="w-full px-4 py-3.5 bg-surface-graphite/50 border border-surface-slate/30 rounded-xl text-accent-white placeholder:text-accent-silver/50 focus:border-brand-orange/50 focus:ring-2 focus:ring-brand-orange/20 outline-none transition-all resize-none"
                        placeholder="How can we help you?"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-4 bg-brand-orange hover:bg-brand-orange-light disabled:opacity-50 text-white font-semibold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-brand-orange/25 hover:shadow-brand-orange/40"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          {content.formFields.submitButton}
                          <IconArrowRight size={18} />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </motion.div>

          {/* Sidebar Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2 space-y-8"
          >
            {/* Office Card */}
            <div className="p-6 rounded-2xl bg-surface-coal border border-surface-slate/30">
              <h3 className="text-lg font-semibold text-accent-white mb-6 flex items-center gap-2">
                <IconLocation size={20} className="text-brand-orange" />
                Our Office
              </h3>

              {content.offices.map((office) => (
                <div key={office.city}>
                  <div className="text-brand-orange text-sm font-medium mb-1">{office.type}</div>
                  <div className="text-xl font-semibold text-accent-white mb-2">{office.city}</div>
                  <p className="text-accent-silver whitespace-pre-line mb-3">{office.address}</p>
                  <a href={`tel:${office.phone.replace(/\s/g, '')}`} className="text-accent-platinum hover:text-brand-orange transition-colors">
                    {office.phone}
                  </a>
                </div>
              ))}
            </div>

            {/* Business Hours */}
            <div className="p-6 rounded-2xl bg-surface-coal border border-surface-slate/30">
              <h3 className="text-lg font-semibold text-accent-white mb-6 flex items-center gap-2">
                <IconClock size={20} className="text-brand-orange" />
                Business Hours
              </h3>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-accent-silver">Monday - Friday</span>
                  <span className="text-accent-white font-medium">{siteConfig.businessHours.weekdays}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-accent-silver">Saturday - Sunday</span>
                  <span className="text-accent-white font-medium">{siteConfig.businessHours.weekends}</span>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-surface-slate/30">
                {(() => {
                  const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/London' }));
                  const day = now.getDay();
                  const hour = now.getHours();
                  const isOpen = day >= 1 && day <= 5 && hour >= 9 && hour < 17;
                  return isOpen ? (
                    <div className="flex items-center gap-2 text-emerald-400">
                      <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                      <span className="text-sm font-medium">We're open now</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-accent-silver">
                      <div className="w-2 h-2 rounded-full bg-accent-silver" />
                      <span className="text-sm font-medium">Currently closed</span>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Quick Contact */}
            <div className="p-6 rounded-2xl bg-gradient-to-br from-brand-orange/20 to-brand-orange/5 border border-brand-orange/20">
              <h3 className="text-lg font-semibold text-accent-white mb-3">
                Need urgent help?
              </h3>
              <p className="text-accent-silver mb-4">
                Call our support line for immediate assistance with your order or product.
              </p>
              <a
                href={`tel:${siteConfig.contact.phone.replace(/\s/g, '')}`}
                className="inline-flex items-center gap-2 text-brand-orange hover:text-brand-orange-light font-semibold transition-colors"
              >
                <IconPhone size={18} />
                {siteConfig.contact.phone}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Map */}
      <section className="container-wide pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative h-[300px] md:h-[400px] rounded-3xl overflow-hidden border border-surface-slate/30"
        >
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2519.5!2d-0.3345!3d51.0835!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4875f0a0a0a0a0a0%3A0x0!2s18+Church+Street%2C+Warnham%2C+West+Sussex+RH12+3QW!5e0!3m2!1sen!2suk!4v1"
            width="100%"
            height="100%"
            style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg)' }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="IT Remarketing - Warnham, Horsham, West Sussex"
          />
        </motion.div>
      </section>
    </Layout>
  );
}
